<script>
import _ from 'lodash'

const defaultErrors = {
  gender: null,
  height: null,
  weight: null
}

export default {
  name: 'DemographicsForm',
  props: {
    title: { type: Boolean, default: () => true },
    userForm: {
      type: Object,
      default: () => ({
        gender: null,
        birthDate: new Date(1990, 1, 1),
        height: null,
        weight: null,
        unitHeight: 'cm',
        unitWeight: 'kg'
      })
    },
    errors: {
      type: Object,
      default: () => defaultErrors
    },
    autofocus: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  computed: {
    getConvertedHeight() {
      if (!this.userForm.height) return ['', '']

      const inches = _.round(this.userForm.height / 2.54)
      const feets = _.floor(inches / 12)
      return [feets, inches % 12]
    },
    getWeight() {
      if (!this.userForm.weight) return ''

      const { weight, unitWeight } = this.userForm
      if (unitWeight == 'lbs') {
        return _.round(weight * 2.205, 1)
      }

      return _.round(weight, 1)
    }
  },
  methods: {
    updateHeight(e) {
      const [feets, inches] = this.getConvertedHeight
      const { name } = e.target
      const value = _.round(Number.parseFloat(e.target.value))

      if (name == 'feets') {
        this.userForm.height = _.round((value + inches / 12) * 30.48)
      }
      if (name == 'inches') {
        this.userForm.height = _.round((feets + value / 12) * 30.48)
      }
    },
    updateWeight(e) {
      const value = _.round(Number.parseFloat(e.target.value), 1)

      if (this.userForm.unitWeight == 'lbs') {
        return (this.userForm.weight = value / 2.205)
      }

      return (this.userForm.weight = value)
    }
  }
}
</script>

<template>
  <div class="demographics-form">
    <!-- Demographics -->
    <h2 v-if="title" class="title is-3 is-spaced">Demographics</h2>
    <div class="subtitle is-spaced">
      Your age, height, weight and gender will affect normal reference ranges
      for some of the biomarkers.
    </div>

    <div class="columns">
      <!-- Gender -->
      <div class="column is-half">
        <div class="field">
          <b-field
            label="Gender"
            :type="{ 'is-danger': !!errors.gender }"
            :message="errors.gender"
          >
            <b-select
              v-model="userForm.gender"
              placeholder="Select your gender"
              expanded
              autofocus
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </b-select>
          </b-field>
        </div>
      </div>
      <!-- DOB -->
      <div class="column is-half">
        <div class="field">
          <label class="label">Date of birth</label>
          <div class="control is-fullwidth">
            <b-datepicker
              v-model="userForm.birthDate"
              placeholder="Click to select..."
              icon="calendar-day"
              icon-pack="fas"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-three-fifths">
        <b-field
          label="Height"
          :type="{ 'is-danger': !!errors.height }"
          :message="errors.height"
        >
          <div v-if="userForm.unitHeight === 'cm'" class="control">
            <input v-model="userForm.height" class="input" type="text" />
          </div>
          <b-field v-if="userForm.unitHeight === 'ft'" grouped>
            <span class="control is-expanded">
              <input
                class="input"
                :value="getConvertedHeight[0]"
                name="feets"
                @change="updateHeight"
              />
              <p class="help">ft</p>
            </span>
            <span class="control is-expanded">
              <input
                class="input"
                :value="getConvertedHeight[1]"
                name="inches"
                @change="updateHeight"
              />
              <p class="help">in</p>
            </span>
          </b-field>
        </b-field>
      </div>

      <div class="column is-two-fifths">
        <b-field label="Unit">
          <b-select v-model="userForm.unitHeight" expanded>
            <option>cm</option>
            <option>ft</option>
          </b-select>
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column is-three-fifths">
        <b-field
          label="Weight"
          :type="{ 'is-danger': !!errors.weight }"
          :message="errors.weight"
          expanded
        >
          <input
            class="input"
            :value="getWeight"
            name="weight"
            @change="updateWeight"
          />
        </b-field>
      </div>
      <div class="column is-two-fifths">
        <b-field label="Unit">
          <b-select v-model="userForm.unitWeight" expanded>
            <option>kg</option>
            <option>lbs</option>
          </b-select>
        </b-field>
      </div>
    </div>
  </div>
</template>

<style lang="sass">
.demographics-form
  margin-bottom: 12px
  h2.title
    margin-bottom: 8px
</style>
